import "@/App.css";
import { ATTLaunchPrompt } from "@/components/ATTLaunchPrompt";
import "@/hooks/useTheme";  // bootstrap: applies persisted theme class before mount
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { FullPageLoader } from "@/components/AppShell";

// EAGER — these are on the critical rendering path for a returning logged-in
// user landing on /app or a first-time visitor on /login. Loading them lazily
// would show a spinner immediately before the first paint, which is exactly
// what we're trying to avoid.
import Landing from "@/pages/LandingV2";
import Login from "@/pages/Login";
import IOSOnboarding, { isOnboardingComplete } from "@/pages/IOSOnboarding";
import AppShell from "@/components/AppShell";
import SubscriptionGate from "@/components/SubscriptionGate";
import AIConsentModal from "@/components/AIConsentModal";
import { isNativeApp } from "@/lib/native";
import OfflineBanner from "@/components/OfflineBanner";
import UpdateGate from "@/components/UpdateGate";
import Dashboard from "@/pages/Dashboard";
import AddMeal from "@/pages/AddMeal";

// LAZY — everything else is code-split so the initial bundle only ships the
// screens above. Admin pages alone were ~30% of the bundle and 99% of users
// never open them; onboarding/quiz/checkout only run once per lifetime.
const Intro = lazy(() => import("@/pages/Intro"));
const Quiz = lazy(() => import("@/pages/Quiz"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const Legal = lazy(() => import("@/pages/Legal"));
const HealthDisclaimer = lazy(() => import("@/pages/HealthDisclaimer"));
const Methodology = lazy(() => import("@/pages/Methodology"));
const DeleteAccountRequest = lazy(() => import("@/pages/DeleteAccountRequest"));
const Blog = lazy(() => import("@/pages/Blog"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Activate = lazy(() => import("@/pages/Activate"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const Results = lazy(() => import("@/pages/Results"));
const Progress = lazy(() => import("@/pages/Progress"));
const AICoach = lazy(() => import("@/pages/AICoach"));
const WorkoutsPage = lazy(() => import("@/pages/WorkoutsPage"));
const WorkoutCoursePage = lazy(() => import("@/pages/WorkoutCoursePage"));
const WorkoutSectionPage = lazy(() => import("@/pages/WorkoutSectionPage"));
const AdminWorkouts = lazy(() => import("@/pages/AdminWorkouts"));
const AdminFoods = lazy(() => import("@/pages/AdminFoods"));
const ChatHistory = lazy(() => import("@/pages/ChatHistory"));
const Settings = lazy(() => import("@/pages/Settings"));
const Profile = lazy(() => import("@/pages/Profile"));
const UpdateGoals = lazy(() => import("@/pages/UpdateGoals"));
const MoreMenu = lazy(() => import("@/pages/MoreMenu"));
const More = lazy(() => import("@/pages/More"));
const ProgressPhotos = lazy(() => import("@/pages/ProgressPhotos"));
const Citations = lazy(() => import("@/pages/Citations"));
const SharedProgress = lazy(() => import("@/pages/SharedProgress"));
const SharedProgressPhotos = lazy(() => import("@/pages/SharedProgressPhotos"));
const PublicRecipePage = lazy(() => import("@/pages/PublicRecipePage"));
const LanguagePicker = lazy(() => import("@/pages/LanguagePicker"));
const ChangePassword = lazy(() => import("@/pages/ChangePassword"));
const RecipesPage = lazy(() => import("@/pages/RecipesPage"));
const RecipeDetailPage = lazy(() => import("@/pages/RecipeDetailPage"));
const ImportedRecipeDetailPage = lazy(() => import("@/pages/ImportedRecipeDetailPage"));
const AffiliatePublic = lazy(() => import("@/pages/AffiliatePublic"));
const RedeemRedirect = lazy(() => import("@/pages/RedeemRedirect"));
const ReferralRedirect = lazy(() => import("@/pages/ReferralRedirect"));
const AdminReferrals = lazy(() => import("@/pages/AdminReferrals"));
const RenewSubscription = lazy(() => import("@/pages/RenewSubscription"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));
const PreviewPaywallFlow = lazy(() => import("@/pages/PreviewPaywallFlow"));
const CalendarPage = lazy(() => import("@/pages/CalendarPage"));
const DayDetailPage = lazy(() => import("@/pages/DayDetailPage"));

// Admin — every one of these is lazy because a normal user never routes here.
const AdminLayout = lazy(() => import("@/pages/AdminLayout"));
const AdminAudit = lazy(() => import("@/pages/AdminAudit"));
const AdminTestimonials = lazy(() => import("@/pages/AdminTestimonials"));
const AdminRecipes = lazy(() => import("@/pages/admin/AdminRecipes"));
const AdminBlog = lazy(() => import("@/pages/AdminBlog"));
const AdminBlogEditor = lazy(() => import("@/pages/AdminBlogEditor"));
const AdminBlogAutomation = lazy(() => import("@/pages/AdminBlogAutomation"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const AdminAiUsage = lazy(() => import("@/pages/AdminAiUsage"));
const AdminCoupons = lazy(() => import("@/pages/AdminCoupons"));
const AdminGuideCoupons = lazy(() => import("@/pages/AdminGuideCoupons"));
const AdminAffiliates = lazy(() => import("@/pages/AdminAffiliates"));
const AdminAttribution = lazy(() => import("@/pages/AdminAttribution"));
const AdminFinance = lazy(() => import("@/pages/AdminFinance"));
const AdminLeads = lazy(() => import("@/pages/AdminLeads"));
const AdminSubscriptions = lazy(() => import("@/pages/AdminSubscriptions"));
const AdminPush = lazy(() => import("@/pages/AdminPush"));
const AdminJourney = lazy(() => import("@/pages/AdminJourney"));
const AdminPurchases = lazy(() => import("@/pages/AdminPurchases"));
const AdminStripeRevenue = lazy(() => import("@/pages/AdminStripeRevenue"));
const AdminDiagnoseBuyer = lazy(() => import("@/pages/AdminDiagnoseBuyer"));
const AdminReviews = lazy(() => import("@/pages/AdminReviews"));
const AdminInvites = lazy(() => import("@/pages/AdminInvites"));
const AdminSystem = lazy(() => import("@/pages/AdminSystem"));
const AdminAppHealth = lazy(() => import("@/pages/AdminAppHealth"));
const AdminDiagnostics = lazy(() => import("@/pages/AdminDiagnostics"));
const AdminWebhooks = lazy(() => import("@/pages/AdminWebhooks"));
const AdminLinks = lazy(() => import("@/pages/AdminLinks"));
const AdminABTest = lazy(() => import("@/pages/AdminABTest"));
const AdminOverviewLazy = lazy(() => import("@/pages/Admin").then(m => ({ default: m.AdminOverview })));
const AdminUsersLazy = lazy(() => import("@/pages/Admin").then(m => ({ default: m.AdminUsers })));
const AdminConfigLazy = lazy(() => import("@/pages/Admin").then(m => ({ default: m.AdminConfig })));
const AdminEmailInboxLazy = lazy(() => import("@/pages/AdminEmail").then(m => ({ default: m.AdminEmailInbox })));
const AdminEmailTemplatesLazy = lazy(() => import("@/pages/AdminEmail").then(m => ({ default: m.AdminEmailTemplates })));
const AdminEmailSequencesLazy = lazy(() => import("@/pages/AdminEmail").then(m => ({ default: m.AdminEmailSequences })));
const AdminEmailCampaignsLazy = lazy(() => import("@/pages/AdminEmail").then(m => ({ default: m.AdminEmailCampaigns })));
const AdminEmailLogsLazy = lazy(() => import("@/pages/AdminEmail").then(m => ({ default: m.AdminEmailLogs })));
const DownloadLinks = lazy(() => import("@/pages/DownloadLinks"));
const RecipeMiniQuiz = lazy(() => import("@/pages/RecipeMiniQuiz"));

function PaymentCancel() {
  return (
    <div className="min-h-screen flex items-center justify-center p-5 bg-white">
      <div className="max-w-md w-full text-center">
        <h1 className="text-2xl font-bold mb-2">تم إلغاء الدفع</h1>
        <p className="text-neutral-500 mb-6">يمكنك المحاولة مرة أخرى في أي وقت.</p>
        <a href="/checkout" className="orange-gradient text-white px-6 py-3 rounded-full font-semibold inline-block">حاول مرة أخرى</a>
      </div>
    </div>
  );
}

function ProtectedResults() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" />;
  return <Results />;
}

/**
 * PublicOnly — wraps pages a *logged-out* visitor should see (Landing, Login).
 *
 * If a valid JWT is already in localStorage → user gets bounced straight to
 * the in-app dashboard, so opening the URL feels like launching a native
 * app: no login screen, no CTA to re-tap. This is the standard behaviour
 * for fitness/health apps (MyFitnessPal, Noom, Yazio…) and matches what
 * users expect from a PWA saved to the home screen.
 *
 * Logout still works: `useAuth().logout` wipes the token, at which point
 * this component stops redirecting and the user sees the Landing / Login
 * page again.
 */
function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  // Admin roles (`admin`, `super_admin`, `nutritionist` — anything
  // that isn't literally `"user"`) belong on the admin dashboard, not
  // the calorie-tracker app. Previously we hard-coded `/app` here,
  // which silently dropped freshly-logged-in admins on the user side.
  if (user) {
    const isStaff = user.role && user.role !== "user";
    return <Navigate to={isStaff ? "/admin" : "/app"} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors closeButton toastOptions={{ style: { fontFamily: "'Alexandria', -apple-system, sans-serif", letterSpacing: "normal" } }} />
          <ATTLaunchPrompt />
          <AIConsentModal />
          <OfflineBanner />
          <UpdateGate />
          {/* Suspense fallback = the same full-page loader we already use for
              auth checks, so the transition between eager and lazy routes is
              visually identical. In practice this only appears for a split
              second the first time each lazy chunk is hit; after that the
              browser caches the chunk so subsequent visits are instant. */}
          <Suspense fallback={<FullPageLoader />}>
            <Routes>
              {/* Root landing.
                  On WEB: show the marketing landing page (with Stripe CTAs).
                  In NATIVE (iOS/Android Capacitor): route first-time users
                  through the IOS onboarding (language → welcome slides →
                  signup) which lands them in /quiz → /results → native
                  paywall. Returning users who completed onboarding skip
                  straight to /login. */}
              <Route
                path="/"
                element={
                  // 1) A valid session token trumps everything — send
                  //    the user straight into the app so cold-starts
                  //    don't dump logged-in users at the landing/
                  //    onboarding page (very disorienting on native).
                  // 2) Native + no token: gate through the welcome
                  //    walkthrough on first run, then bounce returning
                  //    installs to /login.
                  // 3) Web + no token: show the marketing landing.
                  (typeof localStorage !== "undefined" && localStorage.getItem("calofay_token"))
                    ? <Navigate to="/app" replace />
                    : isNativeApp()
                      ? (isOnboardingComplete()
                          ? <Navigate to="/login" replace />
                          : <Navigate to="/welcome" replace />)
                      : <PublicOnly><Landing /></PublicOnly>
                }
              />
              {/* Explicit language routes so marketing campaigns can drop
                  visitors straight into their language (`.app/en` or
                  `.app/ar`) without a mid-flight flicker while the
                  landing decides. `LandingV2` reads the URL prefix in
                  its lang bootstrap so these routes work end-to-end. */}
              <Route path="/en" element={<PublicOnly><Landing /></PublicOnly>} />
              <Route path="/ar" element={<PublicOnly><Landing /></PublicOnly>} />
              <Route path="/welcome" element={isNativeApp() ? <IOSOnboarding /> : <Navigate to="/" replace />} />
              <Route path="/signup" element={<IOSOnboarding />} />
              {/* Legacy iOS-prefixed routes — kept as permanent redirects so
                  existing native-app installs, share links, and bookmarks
                  in the wild keep working while the URL bar shows the new
                  platform-neutral `/welcome` and `/signup` paths. */}
              <Route path="/ios/welcome" element={<Navigate to="/welcome" replace />} />
              <Route path="/ios/signup" element={<Navigate to="/signup" replace />} />
              <Route path="/intro" element={<Intro />} />
              <Route path="/quiz" element={<Quiz />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/return" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/activate" element={<Activate />} />
              {/* Login routes logged-in users itself (routeFor) — PublicOnly's blanket /app redirect would race it and drop the checkout-link resume. */}
              <Route path="/login" element={<Login />} />
              {/* Legal pages — all mount the same Legal component with
                  a different defaultTab so URLs stay clean & shareable
                  (search engines index them individually), while an
                  internal tab switcher lets users hop between the three
                  without leaving the SPA. */}
              <Route path="/privacy" element={<Legal defaultTab="privacy" />} />
              <Route path="/terms" element={<Legal defaultTab="terms" />} />
              <Route path="/health-disclaimer" element={<HealthDisclaimer />} />
              <Route path="/methodology" element={<Methodology />} />
              <Route path="/health" element={<Legal defaultTab="health" />} />
              {/* Public account-deletion request page. This URL is registered
                  in Google Play Data-Safety + Apple App Store metadata so
                  users can request deletion WITHOUT logging in. */}
              <Route path="/delete-account" element={<DeleteAccountRequest />} />
              {/* Public one-click unsubscribe landing. Linked from every
                  marketing/transactional email. MUST stay outside the
                  AppShell + SubscriptionGate wrappers so a user who
                  clicked the link from Gmail isn't force-redirected
                  into the paywall before the unsub call fires. */}
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/preview-paywall" element={<PreviewPaywallFlow />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              {/* English blog track — physically distinct URLs so
                  Google indexes each language cleanly and the AR/EN
                  articles don't dilute each other's hreflang signal.
                  The `Blog` + `BlogPost` components detect this
                  prefix via `useLocation()` and fetch `?lang=en`. */}
              <Route path="/en/blog" element={<Blog />} />
              <Route path="/en/blog/:slug" element={<BlogPost />} />
              <Route path="/share/progress/:token" element={<SharedProgress />} />
              <Route path="/share/progress/:token/photos" element={<SharedProgressPhotos />} />
              <Route path="/r/:id" element={<PublicRecipePage />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/sources" element={<Citations />} />
              <Route path="/results" element={<ProtectedResults />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminOverviewLazy />} />
                <Route path="users" element={<AdminUsersLazy />} />
                <Route path="config" element={<AdminConfigLazy />} />
                <Route path="email" element={<Navigate to="/admin/email/templates" replace />} />
                <Route path="email/inbox" element={<AdminEmailInboxLazy />} />
                <Route path="email/templates" element={<AdminEmailTemplatesLazy />} />
                <Route path="email/sequences" element={<AdminEmailSequencesLazy />} />
                <Route path="email/campaigns" element={<AdminEmailCampaignsLazy />} />
                <Route path="email/logs" element={<AdminEmailLogsLazy />} />
                <Route path="coupons" element={<AdminCoupons />} />
                <Route path="guides/coupons" element={<AdminGuideCoupons />} />
                <Route path="affiliates" element={<AdminAffiliates />} />
                <Route path="attribution" element={<AdminAttribution />} />
                <Route path="finance" element={<AdminFinance />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="subscriptions" element={<AdminSubscriptions />} />
                <Route path="push" element={<AdminPush />} />
                <Route path="journey" element={<AdminJourney />} />
                <Route path="purchases" element={<AdminPurchases />} />
                <Route path="stripe-revenue" element={<AdminStripeRevenue />} />
                <Route path="diagnose-buyer" element={<AdminDiagnoseBuyer />} />
                <Route path="referrals" element={<AdminReferrals />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="invites" element={<AdminInvites />} />
                <Route path="system" element={<AdminSystem />} />
                <Route path="app-health" element={<AdminAppHealth />} />
                <Route path="diagnostics" element={<AdminDiagnostics />} />
                <Route path="webhooks" element={<AdminWebhooks />} />
                <Route path="links" element={<AdminLinks />} />
                <Route path="ab-test" element={<AdminABTest />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
                <Route path="recipes" element={<AdminRecipes />} />
                <Route path="workouts" element={<AdminWorkouts />} />
                <Route path="foods" element={<AdminFoods />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="blog/automation" element={<AdminBlogAutomation />} />
                <Route path="blog/new" element={<AdminBlogEditor />} />
                <Route path="blog/:id/edit" element={<AdminBlogEditor />} />
                <Route path="ai-usage" element={<AdminAiUsage />} />
                <Route path="audit" element={<AdminAudit />} />
              </Route>
              <Route path="/affiliate/:token" element={<AffiliatePublic />} />
              <Route path="/join/:code" element={<RedeemRedirect />} />
              <Route path="/invite/:code" element={<ReferralRedirect />} />
              <Route path="/download" element={<DownloadLinks />} />
              <Route path="/download/" element={<DownloadLinks />} />
              {/* Recipe-focused landing (AR-first) — same section
                  sequence as `/` with hero copy and CTA quiz swapped
                  to recipe/AI-chef narrative. Result screen ends in
                  the standard /checkout paywall. */}
              <Route path="/recipe" element={<Landing variant="recipe" />} />
              <Route path="/recipe/" element={<Landing variant="recipe" />} />
              <Route path="/recipe/quiz" element={<RecipeMiniQuiz />} />
              <Route path="/app" element={<SubscriptionGate><AppShell /></SubscriptionGate>}>
                <Route index element={<Dashboard />} />
                <Route path="renew" element={<RenewSubscription />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="day/:date" element={<DayDetailPage />} />
                <Route path="add" element={<AddMeal />} />
                <Route path="recipes" element={<RecipesPage />} />
                <Route path="recipes/:recipeId" element={<RecipeDetailPage />} />
                <Route path="imported/:recipeId" element={<ImportedRecipeDetailPage />} />
                <Route path="calofay-recipes" element={<RecipesPage />} />
                <Route path="progress" element={<Progress />} />
                <Route path="workouts" element={<WorkoutsPage />} />
                <Route path="workouts/:courseId" element={<WorkoutCoursePage />} />
                <Route path="workouts/:courseId/s/:sectionId" element={<WorkoutSectionPage />} />
                <Route path="coach" element={<AICoach />} />
                <Route path="coach/history" element={<ChatHistory />} />
                <Route path="settings" element={<Settings />} />
                <Route path="settings/profile" element={<Profile />} />
                <Route path="settings/goals" element={<UpdateGoals />} />
                <Route path="settings/more" element={<MoreMenu />} />
                <Route path="progress/photos" element={<ProgressPhotos />} />
                <Route path="photos" element={<ProgressPhotos />} />
                <Route path="settings/language" element={<LanguagePicker />} />
                <Route path="settings/password" element={<ChangePassword />} />
                <Route path="more" element={<More />} />
                <Route path="sources" element={<Citations />} />
              </Route>
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
